/**
 * Detection / SIEM-SOAR Gallery - supported integrations catalogue.
 *
 * Live data sources:
 *   GET  /v1/detection/siem-adapters/registry  → 29-vendor catalogue
 *   GET  /v1/detection/siem-adapters           → tenant-configured adapters
 *   POST /v1/detection/siem-adapters/:id/test  → send a test dispatch
 *
 * Phase 0 (2026-05-22) - each catalogue tile is clickable and
 * deep-links into the unified marketplace at `/admin/integrations`
 * with the vendor pre-filtered. The configured-adapters section
 * above retains its per-adapter test button.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Search, Grid3x3, Cable, Loader2, AlertTriangle, CheckCircle2, Zap, ArrowRight, History, ChevronDown, ChevronUp } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import {
  listSiemAdapters,
  listSiemAdapterRegistry,
  testSiemAdapter,
  getSiemDispatchHistory,
  type SiemAdapter,
  type SiemAdapterDescriptor,
  type SiemDispatchHistoryEntry,
} from '@/lib/api';

type Kind = 'siem' | 'soar' | 'generic';

function classifyKind(descriptor: SiemAdapterDescriptor): Kind {
  const id = descriptor.id.toLowerCase();
  if (/webhook|syslog|kafka|generic/.test(id)) return 'generic';
  if (/xsoar|soar|tines|torq|swimlane|servicenow|resilient|insightconnect|d3/.test(id)) return 'soar';
  return 'siem';
}

interface TestResult {
  adapterId: string;
  ok: boolean;
  message: string;
}

export function DetectionSiemGallery() {
  const [registry, setRegistry] = useState<SiemAdapterDescriptor[]>([]);
  const [configured, setConfigured] = useState<SiemAdapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | Kind>('all');
  const [testing, setTesting] = useState<string | null>(null);
  const [lastTest, setLastTest] = useState<TestResult | null>(null);
  // P1-6 (2026-05-26) - per-adapter dispatch-history toggle + cache.
  const [historyOpen, setHistoryOpen] = useState<Record<string, boolean>>({});
  const [historyEntries, setHistoryEntries] = useState<
    Record<string, SiemDispatchHistoryEntry[]>
  >({});
  const [historyLoading, setHistoryLoading] = useState<Record<string, boolean>>({});

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([listSiemAdapterRegistry(), listSiemAdapters()]).then(([rRes, cRes]) => {
      if (signal?.aborted) return;
      if (rRes.status === 'fulfilled') setRegistry(rRes.value.adapters);
      if (cRes.status === 'fulfilled') setConfigured(cRes.value.adapters);
      const errs: string[] = [];
      if (rRes.status === 'rejected') errs.push(`registry: ${(rRes.reason as Error)?.message ?? 'failed'}`);
      if (cRes.status === 'rejected') errs.push(`configured: ${(cRes.reason as Error)?.message ?? 'failed'}`);
      setError(errs.length > 0 ? errs.join(' · ') : null);
      setLoading(false);
    });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const filtered = useMemo(() => {
    return registry.filter((a) => {
      const kind = classifyKind(a);
      if (filter !== 'all' && kind !== filter) return false;
      if (search && !`${a.displayName} ${a.vendor}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [registry, filter, search]);

  const kindCounts = useMemo(() => ({
    all:     registry.length,
    siem:    registry.filter((a) => classifyKind(a) === 'siem').length,
    soar:    registry.filter((a) => classifyKind(a) === 'soar').length,
    generic: registry.filter((a) => classifyKind(a) === 'generic').length,
  }), [registry]);

  async function onTest(adapter: SiemAdapter) {
    setTesting(adapter.id);
    setLastTest(null);
    try {
      const r = await testSiemAdapter(adapter.id);
      setLastTest({
        adapterId: adapter.id,
        ok: r.ok,
        message: `Test event dispatched to ${r.vendor} at ${new Date(r.sentAt).toLocaleTimeString()}.`,
      });
      await refresh();
      // P1-6 - if the history panel is open for this adapter, refresh
      // it inline so the new dispatch appears at the top.
      if (historyOpen[adapter.id]) {
        void loadHistory(adapter.id);
      }
    } catch (err) {
      setLastTest({
        adapterId: adapter.id,
        ok: false,
        message: err instanceof Error ? err.message : 'test failed',
      });
    } finally {
      setTesting(null);
    }
  }

  // P1-6 (2026-05-26) - fetch + toggle the per-adapter dispatch history.
  async function loadHistory(adapterId: string): Promise<void> {
    setHistoryLoading((s) => ({ ...s, [adapterId]: true }));
    try {
      const r = await getSiemDispatchHistory(adapterId, 10);
      setHistoryEntries((s) => ({ ...s, [adapterId]: r.entries }));
    } catch {
      setHistoryEntries((s) => ({ ...s, [adapterId]: [] }));
    } finally {
      setHistoryLoading((s) => ({ ...s, [adapterId]: false }));
    }
  }
  function toggleHistory(adapterId: string): void {
    const willOpen = !historyOpen[adapterId];
    setHistoryOpen((s) => ({ ...s, [adapterId]: willOpen }));
    if (willOpen && !historyEntries[adapterId]) {
      void loadHistory(adapterId);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="SIEM / SOAR Gallery"
        subtitle="29 connectable vendors. The catalogue covers every IBM QRadar / Splunk / Sentinel / Cortex XSOAR / Chronicle / ArcSight / ServiceNow SecOps plus generic webhook / syslog-CEF / Kafka shells."
        badges={[
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [
                  { label: `${registry.length} adapters`, variant: 'glass' as const },
                  { label: `${configured.length} configured`, variant: configured.length > 0 ? ('success' as const) : ('glass' as const) },
                ]),
        ]}
      />

      {loading && (
        <LoadingState caption="Loading SIEM / SOAR catalogue…" />
      )}
      {error && !loading && (
        <ErrorState
          title="Some adapter data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          {/* Configured adapters - the live wire */}
          {configured.length > 0 && (
            <GlassCard className="p-5 mb-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Configured for this tenant</CardTitle>
                <CardDescription>Per-adapter test dispatch sends a tagged "this is a test" event without firing real SOC tickets.</CardDescription>
              </CardHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {configured.map((a) => (
                  <div key={a.id} className="glass-lite rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <Cable className="h-4 w-4 text-primary shrink-0" />
                        <code className="text-xs font-mono capitalize truncate">{a.vendor}</code>
                      </div>
                      <Badge
                        variant={a.status === 'active' ? 'success' : a.status === 'paused' ? 'warning' : 'glass'}
                        className="text-[9px] capitalize"
                      >
                        {a.status}
                      </Badge>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      ID: <code className="font-mono">{a.id.slice(0, 18)}…</code>
                    </div>
                    <Button
                      variant="glass"
                      size="sm"
                      className="w-full"
                      onClick={() => onTest(a)}
                      disabled={testing === a.id}
                    >
                      {testing === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                      {testing === a.id ? 'Dispatching…' : 'Send test event'}
                    </Button>
                    {/* P1-6 (2026-05-26) - expandable last-N test
                        dispatch history per adapter. Reads from a
                        cap-50 in-memory ring buffer on the server. */}
                    <button
                      type="button"
                      onClick={() => toggleHistory(a.id)}
                      className="w-full inline-flex items-center justify-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors py-1"
                      aria-expanded={historyOpen[a.id] ? 'true' : 'false'}
                    >
                      <History className="h-3 w-3" />
                      {historyOpen[a.id] ? 'Hide' : 'Show'} dispatch history
                      {historyOpen[a.id] ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                    </button>
                    {historyOpen[a.id] && (
                      <div className="border-t border-border/30 pt-2 space-y-1">
                        {historyLoading[a.id] ? (
                          <div className="text-[10px] text-muted-foreground py-2 text-center">
                            Loading…
                          </div>
                        ) : (historyEntries[a.id] ?? []).length === 0 ? (
                          <div className="text-[10px] text-muted-foreground py-1">
                            No test dispatches yet - click "Send test event" above to record one.
                          </div>
                        ) : (
                          <ul className="text-[10px] space-y-0.5 max-h-32 overflow-y-auto">
                            {(historyEntries[a.id] ?? []).map((e, i) => (
                              <li
                                key={`${e.sentAt}-${i}`}
                                className="flex items-center gap-1.5"
                                title={e.errorMessage ?? ''}
                              >
                                {e.ok ? (
                                  <CheckCircle2 className="h-3 w-3 text-success shrink-0" />
                                ) : (
                                  <AlertTriangle className="h-3 w-3 text-warning shrink-0" />
                                )}
                                <span className="font-mono text-muted-foreground">
                                  {new Date(e.sentAt).toLocaleString()}
                                </span>
                                <span className="capitalize text-muted-foreground/80">
                                  {e.vendor}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {lastTest && (
                <div className={`glass-lite border rounded-md p-3 mt-3 text-xs flex items-start gap-2 ${lastTest.ok ? 'border-success/40' : 'border-sev-high/40'}`}>
                  {lastTest.ok ? <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="font-medium">{lastTest.ok ? 'Test event dispatched' : 'Test dispatch failed'}</div>
                    <div className="text-muted-foreground mt-0.5">{lastTest.message}</div>
                  </div>
                  <Button size="sm" variant="glass" onClick={() => setLastTest(null)}>Dismiss</Button>
                </div>
              )}
            </GlassCard>
          )}

          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="glass-lite flex items-center gap-2 rounded-md px-3 py-1.5 flex-1 min-w-[260px] max-w-md">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vendor or product..."
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | Kind)}>
              <TabsList>
                <TabsTrigger value="all">All ({kindCounts.all})</TabsTrigger>
                <TabsTrigger value="siem">SIEM ({kindCounts.siem})</TabsTrigger>
                <TabsTrigger value="soar">SOAR ({kindCounts.soar})</TabsTrigger>
                <TabsTrigger value="generic">Generic ({kindCounts.generic})</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Catalogue */}
          {filtered.length === 0 ? (
            <div className="glass-lite rounded-md p-6 text-center text-sm text-muted-foreground">
              No adapters match the current filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((a, i) => {
                const kind = classifyKind(a);
                return (
                  <motion.div
                    key={a.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.4) }}
                  >
                    <GlassCard shine className="p-4 h-full flex flex-col">
                      <div className="flex items-start justify-between mb-2">
                        <div className={cn(
                          'h-9 w-9 rounded-md flex items-center justify-center text-white text-xs font-bold',
                          kind === 'siem' && 'bg-info',
                          kind === 'soar' && 'bg-warning',
                          kind === 'generic' && 'bg-muted-foreground',
                        )}>
                          <Grid3x3 className="h-4 w-4" />
                        </div>
                        <Badge variant={kind === 'siem' ? 'info' : kind === 'soar' ? 'warning' : 'glass'} className="text-[9px] uppercase">
                          {kind}
                        </Badge>
                      </div>
                      <h3 className="text-sm font-semibold leading-tight mb-1">{a.displayName}</h3>
                      <p className="text-[11px] text-muted-foreground">{a.vendor}</p>
                      <div className="space-y-1 mt-3 pt-3 border-t border-border/30 text-[10px] text-muted-foreground">
                        <div>Auth · <code className="text-foreground/90">{a.authMethod}</code></div>
                        <div>Wire · <code className="text-foreground/90">{a.protocol}</code></div>
                      </div>
                      <Button
                        variant="glass"
                        size="sm"
                        className="w-full mt-3"
                        asChild
                      >
                        <Link
                          to="/admin/integrations"
                          search={{ q: a.id } as never}
                          title={`Configure ${a.displayName} in the unified marketplace`}
                        >
                          <Cable className="h-3 w-3" /> Configure
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      </Button>
                    </GlassCard>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
